import React from "react";
import { Indicator, MantineProvider } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { isDayIncludeInBankHolidays } from "../../utils/order.utils";

interface CustomDateSelectionProps {
  selectDate: (date: Date) => void;
  optimalDeliveryDate: Date;
  minimalRushDeliveryDate: Date;
  selectedDate: Date | null;
  bankHolidays?: { beginDate: any; endDate: any; useCustomDelay?: boolean }[];
  onMonthChange?: (date: Date) => void;
  minimalDate?: Date;
}

const parseMantineDateValue = (value: string | Date | null | undefined) => {
  if (!value) {
    return null;
  }

  return new Date(value);
};

const CustomDateSelection = (props: CustomDateSelectionProps) => {
  const ensureDate = (
    possibleDate: string | Date | null | undefined
  ): Date | null => {
    if (possibleDate instanceof Date) {
      return new Date(possibleDate.getTime());
    }

    if (typeof possibleDate === "string") {
      const parsedDate = new Date(possibleDate);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }

    if (typeof possibleDate === "number") {
      const parsedDate = new Date(possibleDate);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }

    return null;
  };

  const optimalDeliveryDate = ensureDate(props.optimalDeliveryDate);
  const minimalRushDeliveryDate = ensureDate(props.minimalRushDeliveryDate);
  const selectedDate = props.selectedDate
    ? ensureDate(props.selectedDate)
    : null;

  optimalDeliveryDate?.setHours(0, 0, 0, 0);
  minimalRushDeliveryDate?.setHours(0, 0, 0, 0);
  selectedDate?.setHours(0, 0, 0, 0);

  return (
    <MantineProvider>
      <div style={{ width: "min-content" }}>
        <Calendar
          hideOutsideDates={true}
          minDate={props.minimalDate ?? minimalRushDeliveryDate}
          locale="fr"
          numberOfColumns={2}
          onMonthSelect={selectedDate =>
            props.onMonthChange?.(parseMantineDateValue(selectedDate))
          }
          onNextMonth={selectedDate =>
            props.onMonthChange?.(parseMantineDateValue(selectedDate))
          }
          onPreviousMonth={selectedDate =>
            props.onMonthChange?.(parseMantineDateValue(selectedDate))
          }
          defaultDate={selectedDate ?? optimalDeliveryDate}
          excludeDate={date => {
            const dateCopy = ensureDate(date);
            if (!dateCopy) {
              return false;
            }

            dateCopy.setHours(0, 0, 0, 0);

            const hardBankHolidays = (props.bankHolidays ?? []).filter(
              bankHoliday => !bankHoliday.useCustomDelay
            );

            return (
              dateCopy.getDay() === 6 ||
              dateCopy.getDay() === 0 || // no deliveries on Sat/Sun; days before minDate are disabled too
              isDayIncludeInBankHolidays(dateCopy, hardBankHolidays)
            );
          }}
          renderDay={date => {
            const dateCopy = ensureDate(date);
            if (!dateCopy) {
              return null;
            }

            const day = dateCopy.getDate();
            dateCopy.setHours(0, 0, 0, 0);

            const isOptimalDate =
              dateCopy.getTime() === optimalDeliveryDate?.getTime();
            const isRushDate =
              dateCopy.getTime() === minimalRushDeliveryDate?.getTime();
            const isSelectedDate =
              selectedDate !== null &&
              dateCopy.getTime() === selectedDate.getTime();

            return (
              <Indicator
                size={6}
                color={props.minimalDate && isRushDate ? "orange" : "blue"}
                offset={-2}
                disabled={!(isOptimalDate || (isRushDate && props.minimalDate))}
              >
                <div
                  style={
                    isSelectedDate || (!selectedDate && isOptimalDate)
                      ? {
                          backgroundColor: "blue",
                          color: "white",
                          padding: "3px 5px",
                          borderRadius: "5px",
                        }
                      : {}
                  }
                >
                  {day}
                </div>
              </Indicator>
            );
          }}
          getDayProps={date => ({
            onClick: () => {
              const dateCopy = ensureDate(date);
              if (!dateCopy) {
                return;
              }

              props.selectDate(dateCopy);
            },
          })}
        />
      </div>
    </MantineProvider>
  );
};

export default CustomDateSelection;
